import { CONTEXT_LEVEL_LABELS } from '@raro/domain';
import { Figure, Label, Lightbox, Serif, Thumb, cx } from '@raro/ui';
import { useEffect, useState } from 'react';
import type { SourceBrief } from '../lib/types';

const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

/**
 * Ficha prática da fonte: o que é, foco, onde é bom, como ajuda o cliente e o ciclo, melhores momentos ilustrados
 * com os quadros guardados pelo worker. Clicar num momento leva ao trecho daquele minuto.
 */
export function SourceBriefCard({ brief, frames, onJump }: { brief: SourceBrief; frames: { atMs: number; url: string; thumbUrl?: string | null }[]; onJump: (atMs: number) => void }) {
  // capa: o quadro que a Machine escolheu como o de mais contexto; senão, o primeiro
  const [hero, setHero] = useState<number>(brief.heroAtMs ?? frames[0]?.atMs ?? 0);
  const [open, setOpen] = useState<number | null>(null);
  // orientação pelo primeiro quadro: vídeo em pé ganha capa 9:16 numa coluna estreita e miniaturas em pé, sem cortar a cabeça
  const [vertical, setVertical] = useState(false);
  useEffect(() => {
    const first = frames[0]?.url;
    if (!first) return;
    const img = new Image();
    img.onload = () => setVertical(img.naturalHeight > img.naturalWidth);
    img.src = first;
  }, [frames]);
  const ratio = vertical ? '9 / 16' : '16 / 10';
  const thumb = vertical ? { size: 44, ratio } : { size: 72, ratio };
  const openAt = (atMs: number) => { const i = frames.findIndex((f) => f.atMs === atMs); setOpen(i >= 0 ? i : 0); };
  const nearest = (atMs: number) => frames.length ? frames.reduce((a, b) => (Math.abs(b.atMs - atMs) < Math.abs(a.atMs - atMs) ? b : a)) : null;
  const heroFrame = nearest(hero);
  const isVideo = frames.length > 1;
  return (
    <section className={cx('rr-brief', vertical && 'rr-brief--vertical')}>
      <div className="rr-brief__grid">
        <div className="rr-stack rr-stack--4">
          <div className="rr-stack rr-stack--2">
            <Label>a ficha · pela Machine</Label>
            {brief.context && (
              <p className={cx('rr-brief__context', `rr-brief__context--${brief.context.level}`)}>
                <b>confiabilidade desta leitura: {brief.context.score}/100 · {CONTEXT_LEVEL_LABELS[brief.context.level]}.</b>{' '}
                {!brief.context.briefingDefined || !brief.context.questionDefined || !brief.context.objectiveDefined ? `${[!brief.context.briefingDefined && 'briefing do cliente não definido', !brief.context.objectiveDefined && 'ciclo sem objetivo', !brief.context.questionDefined && 'ciclo sem pergunta'].filter(Boolean).join(' · ')}. leia como pista, não como conclusão.` : 'briefing e objetivo do ciclo definidos.'}
              </p>
            )}
            <p className="rr-brief__what"><Serif italic>{brief.what}</Serif></p>
            <p className="rr-muted"><b>foco:</b> {brief.focus}</p>
          </div>
          <div className="rr-brief__cols">
            {brief.strengths.length > 0 && <div><Label>onde é bom</Label><ul className="rr-brief__list">{brief.strengths.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
            {brief.forClient.length > 0 && <div><Label>para o cliente</Label><ul className="rr-brief__list">{brief.forClient.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
            {brief.forCycle.length > 0 && <div><Label>para o ciclo VER</Label><ul className="rr-brief__list rr-brief__list--accent">{brief.forCycle.map((t, i) => <li key={i}>{t}</li>)}</ul></div>}
          </div>
          {brief.gaps.length > 0 && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}><b>não cobre:</b> {brief.gaps.join(' · ')}</p>}
        </div>
        {heroFrame && (
          <div className="rr-stack rr-stack--2">
            <Figure src={heroFrame.url} ratio={ratio} radius="md" onClick={() => openAt(heroFrame.atMs)} />
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-2xs)', letterSpacing: 'var(--rr-tracking-label)', textTransform: 'uppercase' }}>{isVideo ? `quadro em ${mmss(heroFrame.atMs)} · ` : ''}clique para ampliar</span>
          </div>
        )}
      </div>
      {brief.bestMoments.length > 0 && (
        <div className="rr-stack rr-stack--2">
          <Label>veja primeiro</Label>
          <div className="rr-brief__moments">
            {brief.bestMoments.map((m, i) => {
              const f = nearest(m.atMs);
              return (
                <div key={i} className={cx('rr-brief__moment', f && f.atMs === heroFrame?.atMs && 'rr-brief__moment--on')} onMouseEnter={() => f && setHero(f.atMs)}>
                  {f && isVideo ? <button type="button" className="rr-brief__thumb" onClick={() => openAt(f.atMs)} aria-label="ampliar"><Thumb src={f.thumbUrl ?? f.url} size={thumb.size} ratio={thumb.ratio} /></button> : null}
                  <button type="button" className="rr-brief__moment-text" onClick={() => onJump(m.atMs)}><b>{isVideo ? mmss(m.atMs) : `#${i + 1}`}</b> {m.why}</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isVideo && frames.length > 2 && (
        <div className="rr-brief__strip">
          {frames.map((f) => (
            <button type="button" key={f.atMs} className={cx('rr-brief__frame', f.atMs === heroFrame?.atMs && 'rr-brief__frame--on')} onMouseEnter={() => setHero(f.atMs)} onClick={() => openAt(f.atMs)} title={`ampliar · ${mmss(f.atMs)}`}>
              <Thumb src={f.thumbUrl ?? f.url} size={vertical ? 40 : 64} ratio={ratio} />
              <span>{mmss(f.atMs)}</span>
            </button>
          ))}
        </div>
      )}
      <Lightbox
        items={frames.map((f) => ({ src: f.url, caption: isVideo ? `quadro em ${mmss(f.atMs)}` : brief.what.slice(0, 80) }))}
        index={open}
        onClose={() => setOpen(null)}
        onIndex={(i) => setHero(frames[i]?.atMs ?? 0)}
        actions={isVideo ? (_item, i) => <button type="button" className="rr-budget__toggle" onClick={() => { setOpen(null); onJump(frames[i]?.atMs ?? 0); }}>ir ao trecho →</button> : undefined}
      />
    </section>
  );
}
